import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaEnvelope, FaIdCard, FaUserTag, FaCalendarAlt} from 'react-icons/fa';
import axios from '../api/axios';

const UserRole = {
  ADMIN: 'admin',
  USERMANAGEMENT: 'userManagement',
  USER: 'user'
};

const RoleColors = {
  [UserRole.ADMIN]: 'bg-red-100 text-red-800',
  [UserRole.USERMANAGEMENT]: 'bg-yellow-100 text-yellow-800',
  [UserRole.USER]: 'bg-green-100 text-green-800'
};

export default function UserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (err) {
        setError('Error al cargar los detalles del usuario. Por favor, intente de nuevo.');
        console.error('Error fetching user details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <Link to="/home/usuarios" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de usuarios
        </Link>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded" role="alert">
          <p className="font-bold">Advertencia</p>
          <p>No se encontró el usuario</p>
        </div>
        <Link to="/home/usuarios" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800">
          <FaArrowLeft className="mr-2" /> Volver a la lista de usuarios
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/home/usuarios" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors duration-200">
        <FaArrowLeft className="mr-2" /> Volver a la lista de usuarios
      </Link>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-bold flex items-center">
              <FaUser className="mr-4 text-4xl" />
              {user.nombreCompleto}
            </h2>
          </div>
          <p className="mt-2 text-blue-100 flex items-center">
            <FaIdCard className="mr-2" /> ID: {id}
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaIdCard className="mr-2 text-blue-600" /> Información Personal
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">Nombre Completo:</strong> {user.nombreCompleto}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Usuario:</strong> {user.usuario}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaEnvelope className="mr-2 text-green-600" /> Contacto
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">Email:</strong> {user.email}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaUserTag className="mr-2 text-purple-600" /> Rol y Permisos
            </h3>
            <p className="text-gray-700">
              <strong className="text-gray-900">Rol:</strong> 
              <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${RoleColors[user.role]}`}>
                {user.role}
              </span>
            </p>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaCalendarAlt className="mr-2 text-orange-600" /> Información Adicional
            </h3>
            <p className="text-gray-700"><strong className="text-gray-900">Fecha de Registro:</strong> {new Date(user.createdAt).toLocaleDateString()}</p>
            <p className="text-gray-700"><strong className="text-gray-900">Última Actualización:</strong> {new Date(user.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}